<template>
  <v-app>
    <AssignmentNotificationCard />
    <v-app-bar v-show="!matchDisplay.fullscreen" :model-value="!matchDisplay.fullscreen" class="cs-header" :class="{ 'cs-header--development': isDevelopmentSite }" :height="mobileHeader ? 64 : 72" :extension-height="currentRoles.roles.value.length ? 68 : 36" flat>
      <div v-if="isDevelopmentSite" class="header-matrix" aria-hidden="true">
        <span v-for="column in 48" :key="column" :style="{ left: `${(column - 1) * 100 / 48}%`, animationDuration: `${7 + column % 5}s`, animationDelay: `${column * -.83}s` }">01アカサタナハマヤラワ1010アイウエオ01</span>
      </div>
      <div class="header-brand">
      <router-link to="/" class="brand-link" :aria-label="t('header.home')">
        <img
          :src="brandLogo"
          alt=""
        />
        <strong class="brand-wordmark">Coach<span>Sight</span></strong>

      </router-link>
      <span id="header-demo-stamp" />
      </div>

      <v-chip v-if="environmentLabel && !mobileHeader" size="small" class="ml-3 environment-chip">
        {{ environmentLabel }}
      </v-chip>

      <v-chip v-if="authStore.isMasquerading && !mobileHeader" size="small" color="warning" class="ml-2">
        {{ t('header.masqueradingAs', { name: currentUserName }) }}
      </v-chip>

      <router-link
        v-if="currentClub && !mobileHeader"
        class="header-club-link"
        :to="`/clubhouse/${currentClub.id}`"
        :aria-label="t('header.clubHome')"
      >
        <span class="header-club-link__logo">
          <img v-if="currentClub.logo" :src="currentClub.logo" alt="" />
          <v-icon v-else icon="mdi-shield-outline" size="20" />
        </span>
        <span>{{ currentClub.name }}</span>
      </router-link>

      <v-spacer />
      <div id="event-header-actions" class="d-flex align-center" />

      <v-tooltip v-if="betaStore.eligible && !mobileHeader" location="bottom">
        <template #activator="{ props }">
          <v-btn v-bind="props" icon variant="text" class="header-beta-toggle"
            :class="{ 'header-beta-toggle--enabled': betaStore.enabled }"
            :aria-pressed="betaStore.enabled"
            :aria-label="t(betaStore.enabled ? 'header.betaOff' : 'header.betaOn')"
            @click="betaStore.toggle()">
            <span class="header-beta-symbol" aria-hidden="true">β</span>
          </v-btn>
        </template>
        <span>{{ t(betaStore.enabled ? 'header.betaOff' : 'header.betaOn') }}</span>
      </v-tooltip>

      <div class="header-phone-hidden">
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-comment-quote"
              variant="text"
              color="white"
              :aria-label="t('header.support')"
              @click="openSupport"
            />
          </template>
          <span>{{ t('header.support') }}</span>
        </v-tooltip>
      </div>

      <v-tooltip v-if="!mobileHeader" location="bottom">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            :icon="uiStatusStore.isSaving ? 'mdi-record-circle' : 'mdi-refresh'"
            variant="text"
            :color="uiStatusStore.isSaving ? 'error' : 'white'"
            :class="{ 'header-save-indicator': uiStatusStore.isSaving }"
            :aria-label="uiStatusStore.isSaving ? t('header.saving') : t('header.refresh')"
            :disabled="uiStatusStore.isSaving"
            @click="forceReload"
          />
        </template>
        <span>{{ uiStatusStore.isSaving ? t('header.saving') : t('header.refresh') }}</span>
      </v-tooltip>

      <CaptureConnectivityIndicator />

      <div v-if="betaStore.enabled" class="header-phone-hidden">
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-television-box"
              variant="text"
              :color="tvPairingCode ? 'success' : 'white'"
              :aria-label="t('header.tvPairing')"
              @click="showTVDialog = true"
            />
          </template>
          <span>{{ t('header.tvPairing') }}</span>
        </v-tooltip>
      </div>

      <div v-if="betaStore.enabled" class="header-phone-hidden">
        <v-menu location="bottom end">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon="mdi-translate"
              variant="text"
              color="white"
              :aria-label="t('locale.language')"
            />
          </template>
          <v-list min-width="230">
            <v-list-subheader>{{ t('locale.language') }}</v-list-subheader>
            <v-list-item
              v-for="option in localeOptions"
              :key="option.code"
              :title="option.label"
              :prepend-icon="localeStore.locale === option.code ? 'mdi-check' : 'mdi-blank'"
              @click="localeStore.setLocale(option.code)"
            />
          </v-list>
        </v-menu>
      </div>

      <v-menu v-if="mobileHeader" location="bottom end">
        <template #activator="{ props }"><v-btn v-bind="props" icon="mdi-dots-horizontal" variant="text" color="white" :aria-label="t('header.moreActions')" /></template>
        <v-list min-width="240">
          <v-list-item v-if="currentClub" :title="currentClub.name" prepend-icon="mdi-shield-outline" :to="`/clubhouse/${currentClub.id}`" />
          <v-list-item :title="uiStatusStore.isSaving ? t('header.saving') : t('header.refresh')" prepend-icon="mdi-refresh" :disabled="uiStatusStore.isSaving" @click="forceReload" />
          <v-list-item :title="t('header.support')" prepend-icon="mdi-comment-quote" @click="openSupport" />
          <v-list-item v-if="betaStore.eligible" :title="t(betaStore.enabled ? 'header.betaOff' : 'header.betaOn')" prepend-icon="mdi-flask-outline" @click="betaStore.toggle()" />
          <v-list-item v-if="betaStore.enabled" :title="t('header.tvPairing')" prepend-icon="mdi-television-box" @click="showTVDialog = true" />
          <template v-if="betaStore.enabled">
          <v-list-subheader>{{ t('locale.language') }}</v-list-subheader>
          <v-list-item v-for="option in localeOptions" :key="option.code" :title="option.label" :prepend-icon="localeStore.locale === option.code ? 'mdi-check' : 'mdi-blank'" @click="localeStore.setLocale(option.code)" />
          </template>
        </v-list>
      </v-menu>

      <CurrentRolePills v-if="!mobileHeader" :roles="currentRoles.roles.value" :event-nouns="currentRoles.staffNouns.value" :context-name="currentRoles.contextName.value" />
      <v-menu location="bottom end">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            class="profile-button"
            variant="text"
            color="white"
            :aria-label="t('header.userMenu')"
          >
            <v-avatar class="profile-avatar" size="34" color="primary">
              <img v-if="profileImage" class="profile-image" :src="profileImage" alt="" />
              <span v-else>{{ initials }}</span>
            </v-avatar>
          </v-btn>
        </template>

        <v-list min-width="260">
          <v-list-item>
            <v-list-item-title>{{ currentUserName }}</v-list-item-title>
            <v-list-item-subtitle>{{ currentUserEmail }}</v-list-item-subtitle>
          </v-list-item>
          <v-divider />

          <v-list-item :title="t('header.accountSettings')" prepend-icon="mdi-account-cog" @click="goToAccountSettings" />

          <v-divider class="account-menu-phone-actions" />
          <v-list-item
            class="account-menu-phone-actions"
            :title="t('header.support')"
            prepend-icon="mdi-comment-quote"
            @click="openSupport"
          />
          <v-list-item
            v-if="betaStore.enabled"
            class="account-menu-phone-actions"
            :title="t('header.tvPairing')"
            prepend-icon="mdi-television-box"
            @click="showTVDialog = true"
          />
          <template v-if="betaStore.enabled">
          <v-list-subheader class="account-menu-phone-actions">{{ t('locale.language') }}</v-list-subheader>
          <v-list-item
            v-for="option in localeOptions"
            :key="`account-locale-${option.code}`"
            class="account-menu-phone-actions"
            :title="option.label"
            :prepend-icon="localeStore.locale === option.code ? 'mdi-check' : 'mdi-blank'"
            @click="localeStore.setLocale(option.code)"
          />
          </template>

          <v-list-item
            v-if="canMasquerade && !authStore.isMasquerading"
            :title="t('header.masqueradeAsUser')"
            prepend-icon="mdi-account-switch-outline"
            @click="openMasqueradeDialog"
          />
          <v-list-item
            v-if="authStore.isMasquerading"
            :title="t('header.ceaseMasquerade')"
            prepend-icon="mdi-account-cancel"
            @click="handleStopMasquerade"
          />

          <template v-if="canAccessSystemAdmin">
            <v-divider />
            <v-list-item :title="t('header.systemAnalytics')" prepend-icon="mdi-chart-line" @click="goTo('/sysadmin/analytics')" />
            <v-list-item :title="t('header.systemInformation')" prepend-icon="mdi-view-dashboard" @click="goTo('/sysadmin/dashboard')" />
            <v-list-item :title="t('header.platformMaintenance')" prepend-icon="mdi-tools" @click="goTo('/sysadmin/platform-maintenance')" />
          </template>

          <v-divider />
          <v-list-item :title="t('header.about')" prepend-icon="mdi-information" @click="showAboutDialog = true" />
          <v-list-item :title="t('header.signOut')" prepend-icon="mdi-logout" @click="handleLogout" />
        </v-list>
      </v-menu>
      <template v-if="mobileHeader" #extension>
        <div class="header-extension">
        <div class="header-status-row">
          <span v-if="environmentLabel" class="header-environment-status">{{ environmentLabel }}</span>
          <span v-if="authStore.isMasquerading" class="header-masquerade-status">{{ t('header.masqueradingAs', { name: currentUserName }) }}</span>
          <span role="status" class="header-connection-status"><v-icon :icon="isOnline ? 'mdi-wifi' : 'mdi-wifi-off'" :color="isOnline ? 'success' : 'warning'" size="16" />{{ uiStatusStore.isSaving ? t('header.saving') : isOnline ? t('header.online') : t('header.offline') }}</span>
        </div>
        <CurrentRolePills :roles="currentRoles.roles.value" :event-nouns="currentRoles.staffNouns.value" :context-name="currentRoles.contextName.value" />
        </div>
      </template>
    </v-app-bar>

    <v-main>
      <CoachingPublicationRequests />
      <ParentConnectionNotifications />
      <AppBreadcrumbs v-show="!matchDisplay.fullscreen" />
      <div v-if="matchDisplay.fullscreen && route.params.tryoutId && route.query.section !== 'athletes'" class="fullscreen-event-switcher">
        <SeasonEventSwitcher :club-id="String(route.params.clubId)" :season-id="String(route.params.seasonId)" :current-event-id="String(route.params.tryoutId)" />
      </div>
      <div v-if="authStore.switchingIdentity" class="pa-4" role="status"><v-progress-linear indeterminate />{{ t('eventView.loading') }}</div>
      <EventViewBoundary v-else><slot /></EventViewBoundary>
    </v-main>

    <v-dialog v-model="showAboutDialog" max-width="620">
      <v-card class="about-card">
        <div class="about-hero">
          <div class="about-brand">
            <div class="about-logo" aria-hidden="true">
              <img
                :src="brandLogo"
                alt=""
              />
            </div>
            <div>
              <p class="about-eyebrow">{{ t('dialogs.about.eyebrow') }}</p>
              <h2>{{ t('dialogs.about.title') }}</h2>
            </div>
          </div>
          <v-btn
            icon="mdi-close"
            variant="text"
            color="white"
            :aria-label="t('dialogs.about.dismiss')"
            @click="showAboutDialog = false"
          />
        </div>

        <v-card-text class="about-content">
          <p class="about-intro">
            {{ t('dialogs.about.body') }}
          </p>

          <div class="about-highlights" role="list" :aria-label="t('dialogs.about.highlightsLabel')">
            <div v-for="item in aboutHighlights" :key="item.title" class="about-highlight" role="listitem">
              <v-icon :icon="item.icon" size="22" />
              <div>
                <strong>{{ item.title }}</strong>
                <span>{{ item.body }}</span>
              </div>
            </div>
          </div>

          <div class="about-build-panel">
            <div class="about-build-heading">
              <v-icon icon="mdi-package-variant-closed-check" size="20" />
              <strong>{{ t('dialogs.about.buildDetails') }}</strong>
            </div>
            <dl>
              <div>
                <dt>{{ t('dialogs.about.versionLabel') }}</dt>
                <dd>{{ buildInfo.version }}</dd>
              </div>
              <div>
                <dt>{{ t('dialogs.about.environmentLabel') }}</dt>
                <dd>{{ buildInfo.environment }}</dd>
              </div>
              <div>
                <dt>{{ t('dialogs.about.buildLabel') }}</dt>
                <dd>{{ buildInfo.buildNumber }}</dd>
              </div>
              <div v-if="buildInfo.shortCommit">
                <dt>{{ t('dialogs.about.commitLabel') }}</dt>
                <dd>{{ buildInfo.shortCommit }}</dd>
              </div>
            </dl>
          </div>
        </v-card-text>

        <v-card-actions class="about-actions">
          <v-spacer />
          <v-btn color="primary" variant="flat" @click="showAboutDialog = false">{{ t('dialogs.common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="betaStore.enabled" v-model="showTVDialog" max-width="460">
      <v-card>
        <v-card-title>{{ t('dialogs.tv.title') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="tvPairingInput"
            :label="t('dialogs.tv.pairingCode')"
            maxlength="4"
            variant="outlined"
            hide-details="auto"
          />
          <v-alert type="info" variant="tonal" class="mt-4" density="compact">
            {{ t('dialogs.tv.localOnly') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="clearTVPairing">{{ t('dialogs.common.clear') }}</v-btn>
          <v-spacer />
          <span role="status">{{ t(`tvConnection.${tvConnection.status}`) }}</span>
          <v-btn variant="text" @click="showTVDialog = false">{{ t('dialogs.common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveTVPairing">{{ t('dialogs.common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showMasqueradeDialog" max-width="560" :persistent="masquerading" @after-enter="selectedMasqueradeTarget ? masqueradePasswordField?.focus() : masqueradeSearchField?.focus()">
      <v-card class="cs-dialog">
        <form @submit.prevent="handleStartMasquerade">
        <v-card-title class="cs-dialog__heading">{{ t('dialogs.masquerade.title') }}</v-card-title>
        <v-card-text>


          <AppSearchField
            ref="masqueradeSearchField"
            v-model="masqueradeQuery"
            autofocus
            :label="t('dialogs.masquerade.targetEmail')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :loading="masqueradeSearching"
            :disabled="masquerading"
            autocomplete="off"
            @update:model-value="scheduleMasqueradeSearch"
          />

          <v-list v-if="masqueradeTargets.length" class="masquerade-results">
            <v-list-item
              v-for="target in masqueradeTargets"
              :key="target.id"
              :title="target.name || `${target.firstName || ''} ${target.lastName || ''}`.trim() || target.email"
              :subtitle="target.email"
              :active="selectedMasqueradeTarget?.id === target.id"
              :disabled="masquerading"
              color="primary"
              @click="selectMasqueradeTarget(target)"
            />
          </v-list>

          <v-alert v-if="masqueradeError" type="error" variant="tonal" density="compact" class="mt-3">
            {{ masqueradeError }}
          </v-alert>

          <div v-if="selectedMasqueradeTarget" class="cs-dialog__identity">
            <span>{{ t('dialogs.masquerade.selectedUser') }}</span>
            <strong>{{ selectedMasqueradeTarget.name || `${selectedMasqueradeTarget.firstName || ''} ${selectedMasqueradeTarget.lastName || ''}`.trim() || selectedMasqueradeTarget.email }}</strong>
            <span>{{ selectedMasqueradeTarget.email }}</span>
          </div>

          <v-text-field
            ref="masqueradePasswordField"
            v-model="masqueradePassword"
            autocomplete="current-password"
            :disabled="masquerading"
            :label="t('dialogs.masquerade.confirmPassword')"
            type="password"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="mt-4"
            @keydown.enter.prevent.stop="handleStartMasquerade"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="masquerading" @click="closeMasqueradeDialog">{{ t('dialogs.common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            :loading="masquerading"
            :disabled="!canStartMasquerade"
            type="submit"
            variant="flat"
          >
            {{ t('dialogs.masquerade.start') }}
          </v-btn>
        </v-card-actions>
        </form>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import AppSearchField from '@/components/AppSearchField.vue'
import ParentConnectionNotifications from '@/features/notifications/ParentConnectionNotifications.vue'
import CoachingPublicationRequests from '@/features/notifications/CoachingPublicationRequests.vue'
import AssignmentNotificationCard from '@/features/notifications/AssignmentNotificationCard.vue'
import CaptureConnectivityIndicator from '@/components/CaptureConnectivityIndicator.vue'
  import CurrentRolePills from '@/components/CurrentRolePills.vue'
  import { useCurrentRoles } from '@/composables/useCurrentRoles'
  const currentRoles = useCurrentRoles()
  import brandLogo from '@/assets/coachsight-c-logo.svg'
  import { useDisplay } from 'vuetify'
  const { smAndDown: mobileHeader } = useDisplay()
  import { useMatchDisplayStore } from '@/stores/matchDisplay'
  const matchDisplay = useMatchDisplayStore()
  import EventViewBoundary from '@/features/navigation/EventViewBoundary.vue'
  import SeasonEventSwitcher from '@/components/SeasonEventSwitcher.vue'
  import AppBreadcrumbs from '@/features/navigation/AppBreadcrumbs.vue'
  import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'
  import { buildInfo } from '@/app/buildInfo'
  import { useAuth } from '@/auth/composable'
  import { useTVConnectionStore } from '@/stores/tvConnection'
  import { useAuthStore } from '@/stores/auth'
  import { useBetaStore } from '@/stores/beta'
  import { useClubStore } from '@/stores/club'
  import { useLocaleStore } from '@/stores/locale'
  import { useNavigatorStore } from '@/stores/navigator'
  import { useUiStatusStore } from '@/stores/uiStatus'
  import { openMasqueradeTargetKey } from '@/auth/masqueradeDialog'
  import type { MasqueradeTarget } from '@/api'

  const SUPPORT_URL = 'https://flyingdogsoftware.atlassian.net/servicedesk/customer/portal/1'

  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const authStore = useAuthStore()
  const betaStore = useBetaStore()
  const clubStore = useClubStore()
  const localeStore = useLocaleStore()
  const navigatorStore = useNavigatorStore()
  const uiStatusStore = useUiStatusStore()
  const {
    logout,
    searchMasqueradeTargets,
    startMasquerade,
    stopMasquerade,
  } = useAuth()

  const showAboutDialog = ref(false)
  const tvConnection = useTVConnectionStore()
  watch(() => String(authStore.user?.details?.id || authStore.user?.id || ''), id => tvConnection.context(id), { immediate: true })
  onUnmounted(() => tvConnection.stop())
  const showTVDialog = ref(false)
  const isOnline = ref(navigator.onLine)
  const tvPairingCode = ref(localStorage.getItem('tvCode') || '')
  const tvPairingInput = ref(tvPairingCode.value)
  const showMasqueradeDialog = ref(false)
  const masqueradePasswordField = ref<{focus: () => void} | null>(null)
  const masqueradeSearchField = ref<{focus: () => void} | null>(null)
  const masqueradeQuery = ref('')
  const masqueradePassword = ref('')
  const masqueradeTargets = ref<MasqueradeTarget[]>([])
  const selectedMasqueradeTarget = ref<MasqueradeTarget | null>(null)
  const masqueradeSearching = ref(false)
  const masquerading = ref(false)
  const masqueradeError = ref<string | null>(null)
  const masqueradeSearchTimer = ref<number | null>(null)

  const userDetails = computed(() => authStore.user?.details || {})
  const currentUserEmail = computed(() => authStore.user?.email || '')
  const currentUserName = computed(() => {
    const firstName = `${userDetails.value.firstName || ''}`.trim()
    const lastName = `${userDetails.value.lastName || ''}`.trim()
    return authStore.user?.name || `${firstName} ${lastName}`.trim() || currentUserEmail.value
  })
  const initials = computed(() => {
    const nameParts = currentUserName.value.split(/\s+/).filter(Boolean)
    const source = nameParts.length ? nameParts : [currentUserEmail.value]
    return source
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('')
  })
  const profileImage = computed(() => `${userDetails.value.profileImage || ''}`)
  const canMasquerade = computed(() => userDetails.value.systemAdmin === true)
  const canAccessSystemAdmin = canMasquerade
  const localeOptions = computed(() =>
    localeStore.localeOptions.map((option) => ({
      code: option.code,
      label: t(`locale.options.${option.code}`),
    }))
  )
  const isDevelopmentSite = computed(() => ['development', 'dev', 'development-tunnel'].includes(buildInfo.environment.toLowerCase()))
  const environmentLabel = computed(() => {
    if (buildInfo.environment === 'production') return ''
    return buildInfo.environment
  })
  const currentClub = computed(() => {
    const selectedClubId = String(route.params.clubId || navigatorStore.selectedClub || '')
    return selectedClubId ? clubStore.data[selectedClubId] || null : null
  })
  const aboutHighlights = computed(() => [
    {
      icon: 'mdi-clipboard-check-outline',
      title: t('dialogs.about.highlights.selection.title'),
      body: t('dialogs.about.highlights.selection.body'),
    },
    {
      icon: 'mdi-account-group-outline',
      title: t('dialogs.about.highlights.athletes.title'),
      body: t('dialogs.about.highlights.athletes.body'),
    },
    {
      icon: 'mdi-chart-timeline-variant-shimmer',
      title: t('dialogs.about.highlights.decisions.title'),
      body: t('dialogs.about.highlights.decisions.body'),
    },
  ])
  const canStartMasquerade = computed(() =>
    canMasquerade.value && !authStore.isMasquerading &&
    !!selectedMasqueradeTarget.value &&
    !!masqueradePassword.value &&
    !masquerading.value
  )

  const setOnline = () => {
    isOnline.value = navigator.onLine
  }

  const openSupport = () => {
    window.open(SUPPORT_URL, '_blank', 'noopener,noreferrer')
  }

  const forceReload = () => {
    window.location.reload()
  }

  const goTo = (path: string) => {
    router.push(path)
  }

  const goToAccountSettings = () => {
    router.push({
      path: '/manageuser',
      query: {
        returnTo: route.fullPath,
      },
    })
  }

  const saveTVPairing = () => {
    tvPairingCode.value = tvPairingInput.value.trim()
    if (tvPairingCode.value) {
      localStorage.setItem('tvCode', tvPairingCode.value)
      tvConnection.pair(tvPairingCode.value)
    } else {
      tvConnection.unpair()
      localStorage.removeItem('tvCode')
    }
    showTVDialog.value = false
  }

  const clearTVPairing = () => {
    tvConnection.unpair()
    tvPairingCode.value = ''
    tvPairingInput.value = ''
    localStorage.removeItem('tvCode')
  }

  const openMasqueradeDialog = () => {
    masqueradeQuery.value = ''
    masqueradePassword.value = ''
    masqueradeTargets.value = []
    selectedMasqueradeTarget.value = null
    masqueradeError.value = null
    showMasqueradeDialog.value = true
  }

  provide(openMasqueradeTargetKey, target => {
    if (!canMasquerade.value || authStore.isMasquerading || masquerading.value || !target.id || !target.email || target.id === authStore.user?.id) return
    if (masqueradeSearchTimer.value) window.clearTimeout(masqueradeSearchTimer.value)
    openMasqueradeDialog()
    masqueradeQuery.value = target.email
    selectedMasqueradeTarget.value = { ...target }
  })

  const closeMasqueradeDialog = () => {
    if (masquerading.value) return
    showMasqueradeDialog.value = false
  }

  const selectMasqueradeTarget = async (target: MasqueradeTarget) => {
    selectedMasqueradeTarget.value = target
    await nextTick()
    masqueradePasswordField.value?.focus()
  }

  const runMasqueradeSearch = async () => {
    const query = masqueradeQuery.value.trim()
    masqueradeError.value = null
    if (query.length < 3) {
      masqueradeTargets.value = []
      return
    }

    try {
      masqueradeSearching.value = true
      const targets = await searchMasqueradeTargets(query, 8)
      if (query === masqueradeQuery.value.trim() && showMasqueradeDialog.value) masqueradeTargets.value = targets
    } catch (err: any) {
      if (query !== masqueradeQuery.value.trim() || !showMasqueradeDialog.value) return
      masqueradeTargets.value = []
      masqueradeError.value = err.message || t('dialogs.masquerade.searchError')
    } finally {
      masqueradeSearching.value = false
    }
  }

  const scheduleMasqueradeSearch = () => {
    selectedMasqueradeTarget.value = null
    masqueradeTargets.value = []
    if (masqueradeSearchTimer.value) {
      window.clearTimeout(masqueradeSearchTimer.value)
    }
    masqueradeSearchTimer.value = window.setTimeout(runMasqueradeSearch, 300)
  }

  const handleStartMasquerade = async () => {
    if (!canStartMasquerade.value || !selectedMasqueradeTarget.value) return
    try {
      masquerading.value = true
      masqueradeError.value = null
      await startMasquerade(
        masqueradePassword.value,
        selectedMasqueradeTarget.value.id,
        selectedMasqueradeTarget.value.email
      )
      showMasqueradeDialog.value = false
    } catch (err: any) {
      masqueradeError.value = err.message || t('dialogs.masquerade.failure')
    } finally {
      masquerading.value = false
      masqueradePassword.value = ''
    }
  }

  const handleStopMasquerade = async () => {
    try {
      await stopMasquerade()
    } catch (err) {
      console.error('Stop masquerade failed:', err)
    }
  }

  const handleLogout = async () => {
    await logout()
  }

  const syncRouteNavigationState = () => {
    const routeClubId = String(route.params.clubId || '')
    const routeSeasonId = String(route.params.seasonId || '')

    if (routeClubId && navigatorStore.selectedClub !== routeClubId) {
      navigatorStore.justSelectClub(routeClubId)
    }

    if (routeSeasonId && navigatorStore.selectedSeason !== routeSeasonId) {
      navigatorStore.selectSeason(routeSeasonId)
    }
  }

  onMounted(() => {
    window.addEventListener('online', setOnline)
    window.addEventListener('offline', setOnline)
  })

  onUnmounted(() => {
    window.removeEventListener('online', setOnline)
    window.removeEventListener('offline', setOnline)
    if (masqueradeSearchTimer.value) {
      window.clearTimeout(masqueradeSearchTimer.value)
    }
  })

  watch(
    () => [route.params.clubId, route.params.seasonId, authStore.user?.id, authStore.switchingIdentity],
    () => { if (!authStore.switchingIdentity) syncRouteNavigationState() },
    { immediate: true }
  )
</script>

<style scoped>
  .cs-header {
    transition-property: box-shadow;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }

  .cs-header :deep(.v-toolbar__content) {
    padding-inline: 8px;
  }

  .cs-header--development :deep(.v-toolbar__content) { isolation: isolate; }
  .header-matrix {
    position: absolute;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
    background: linear-gradient(90deg, var(--cs-midnight), #071b16 60%, var(--cs-midnight));
  }
  .header-matrix::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgb(9 6 31 / 65%), transparent 40%, rgb(9 6 31 / 45%));
  }
  .header-matrix span {
    position: absolute;
    top: -440px;
    writing-mode: vertical-rl;
    text-orientation: upright;
    color: #75cb91;
    font: 12px/1.4 ui-monospace, monospace;
    letter-spacing: 3px;
    opacity: .32;
    text-shadow: 0 0 7px currentColor;
    animation: header-matrix-fall linear infinite;
  }
  @keyframes header-matrix-fall { to { transform: translateY(900px); } }
  @media (prefers-reduced-motion: reduce) {
    .header-matrix span { animation: none; top: -80px; }
  }

  .header-club-link {
    position: absolute;
    left: 50%;
    top: 50%;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    max-width: min(420px, 36vw);
    min-width: 0;
    padding: 5px 10px;
    border-radius: 8px;
    color: white;
    font-weight: 900;
    text-decoration: none;
    transform: translate(-50%, -50%);
  }

  .header-club-link:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .header-club-link__logo {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    flex: 0 0 auto;
    overflow: hidden;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
  }

  .header-club-link__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .header-club-link > span:last-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .brand-link {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    color: white;
    text-decoration: none;
  }

  .brand-link img {
    width: 34px;
    height: 34px;
    object-fit: contain;
  }

  .brand-link span {
    display: grid;
    line-height: 1.1;
  }

  .brand-link strong {
    font-size: 0.98rem;
  }

  .brand-link small {
    color: rgba(255, 255, 255, 0.74);
    font-size: 0.72rem;
  }

  .environment-chip {
    text-transform: capitalize;
  }

  .account-menu-phone-actions {
    display: none !important;
  }

  .profile-button {
    min-width: 42px;
    width: 42px;
    height: 42px;
    padding-inline: 4px;
  }

  .profile-avatar {
    flex: 0 0 auto;
    overflow: hidden;
  }

  .profile-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  .masquerade-results :deep(.v-list-item-subtitle) { white-space: normal; overflow-wrap: anywhere; }
  .masquerade-results {
    max-height: 220px;
    overflow: auto;
    margin-top: 8px;
    border: 1px solid var(--cs-border);
    border-radius: 6px;
  }

  .about-card {
    overflow: hidden;
    border-radius: 8px;
  }

  .about-hero {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    padding: 28px;
    color: white;
    background: linear-gradient(135deg, #1f1830 0%, #235872 58%, #37866f 100%);
  }

  .about-brand {
    display: flex;
    align-items: center;
    gap: 18px;
    min-width: 0;
  }

  .about-logo {
    display: grid;
    place-items: center;
    width: 72px;
    height: 72px;
    flex: 0 0 auto;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 18px 40px rgba(8, 4, 18, 0.24);
  }

  .about-logo img {
    width: 52px;
    height: 52px;
    object-fit: contain;
  }

  .about-eyebrow {
    margin: 0 0 4px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.77rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  .about-hero h2 {
    margin: 0;
    font-size: 2rem;
    line-height: 1.08;
  }

  .about-content {
    display: grid;
    gap: 20px;
    padding: 24px 28px 18px;
  }

  .about-intro {
    margin: 0;
    color: #3b3646;
    font-size: 1rem;
    line-height: 1.6;
  }

  .about-highlights {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  .about-highlight {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 10px;
    min-height: 116px;
    padding: 14px;
    border: 1px solid #dce6ee;
    border-radius: 8px;
    background: #f8fbfc;
  }

  .about-highlight .v-icon {
    color: #236979;
  }

  .about-highlight div {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  .about-highlight strong {
    color: #1f1830;
    font-size: 0.9rem;
    line-height: 1.22;
  }

  .about-highlight span {
    color: #665f70;
    font-size: 0.82rem;
    line-height: 1.35;
  }

  .about-build-panel {
    display: grid;
    gap: 12px;
    padding: 16px;
    border: 1px solid #e1e5ee;
    border-radius: 8px;
    background: #ffffff;
  }

  .about-build-heading {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #1f1830;
  }

  .about-build-heading .v-icon {
    color: #37866f;
  }

  .about-build-panel dl {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 18px;
    margin: 0;
  }

  .about-build-panel dl > div {
    min-width: 0;
  }

  .about-build-panel dt {
    color: #665f70;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  .about-build-panel dd {
    margin: 2px 0 0;
    color: #1f1830;
    font-weight: 600;
    overflow-wrap: anywhere;
    text-transform: capitalize;
  }

  .header-save-indicator { animation: header-save-pulse 1s ease-in-out infinite; }

  @keyframes header-save-pulse {
    0%, 100% { opacity: 0.65; }
    50% { opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    .header-save-indicator { animation: none; }
  }

  .about-actions {
    padding: 0 28px 24px;
  }

  @media (max-width: 720px) {
    .brand-link small,
    .environment-chip {
      display: none;
    }

    .header-club-link {
      max-width: 34vw;
      padding-inline: 6px;
    }
  }

  @media (max-width: 560px) {
    .header-club-link > span:last-child {
      display: none;
    }

    .header-club-link {
      max-width: 44px;
    }
  }

  @media (max-width: 600px) {
    .header-phone-hidden {
      display: none;
    }

    .account-menu-phone-actions {
      display: flex !important;
    }

    .account-menu-phone-actions.v-divider {
      display: block !important;
    }

    .about-hero {
      padding: 22px;
    }

    .about-logo {
      width: 60px;
      height: 60px;
    }

    .about-logo img {
      width: 44px;
      height: 44px;
    }

    .about-hero h2 {
      font-size: 1.55rem;
    }

    .about-content {
      padding: 20px;
    }

    .about-highlights,
    .about-build-panel dl {
      grid-template-columns: 1fr;
    }

    .about-highlight {
      min-height: 0;
    }

    .about-actions {
      padding: 0 20px 20px;
    }
  }
</style>

<style scoped>
.header-beta-toggle { color: rgba(255, 255, 255, .65); }
.header-beta-symbol { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border: 1px solid currentColor; border-radius: 50%; font-family: Georgia, 'Times New Roman', serif; font-size: 20px; font-weight: normal; line-height: 1; letter-spacing: 0; text-transform: none; }
.header-beta-toggle--enabled { color: white; background-color: rgba(255, 255, 255, .22); }
</style>

<style scoped>
.cs-header { background: linear-gradient(110deg, var(--cs-midnight), var(--cs-indigo)); color: white; border-bottom: 2px solid var(--cs-logo-violet); }
.cs-header :deep(.v-toolbar__content) { padding-inline: 24px; gap: 8px; }
.brand-link { flex-shrink: 0; gap: 8px; }
.brand-link img { width: 46px; height: 46px; }
.brand-link .brand-wordmark { font-family: ui-rounded, 'Arial Rounded MT Bold', system-ui, sans-serif; font-size: 29px; letter-spacing: -1.1px; font-weight: 650; line-height: 1; white-space: nowrap; }
.brand-link .brand-wordmark span { display: inline; color: var(--cs-logo-violet); }
.brand-link:focus-visible, .header-club-link:focus-visible { outline: 2px solid var(--cs-electric); outline-offset: 4px; border-radius: 6px; }
.header-club-link { position: static; transform: none; max-width: min(260px, 20vw); margin-left: 12px; padding-left: 16px; border-left: 1px solid #64517d; border-radius: 0; font-size: .8rem; font-weight: 600; }
.environment-chip { border: 1px solid #64517d; color: var(--cs-soft-white); font-size: .7rem; }
.profile-avatar { border: 1px solid var(--cs-logo-violet); }
.about-hero { background: linear-gradient(110deg, var(--cs-midnight), var(--cs-indigo)); }
.header-extension { width:100%; }
.header-status-row { width: 100%; display: flex; align-items: center; gap: 12px; padding: 0 16px; font-size: .7rem; white-space: nowrap; overflow-x: auto; background: rgba(255,255,255,.04); height: 36px; }
.header-environment-status { color: var(--cs-soft-white); text-transform: capitalize; }
.header-masquerade-status { color: #ffcf80; }
.header-connection-status { display: flex; align-items: center; gap: 5px; }

@media(max-width:959px) {
 .cs-header :deep(.v-toolbar__content) { padding-inline: 12px; gap: 0; }
 .brand-link img { width: 37px; height: 37px; }
 .brand-link .brand-wordmark { font-size: 24px; }
 #event-header-actions { position: absolute; right: 12px; top: 64px; height: 36px; max-width: 45%; overflow-x: auto; }
 .header-status-row { padding-right: 46%; }
 .header-phone-hidden { display: none; }
 .account-menu-phone-actions { display: flex !important; }
}
</style>

<style scoped>
.fullscreen-event-switcher { display: flex; justify-content: flex-end; padding: 8px 12px; background: var(--cs-indigo); }
</style>

<style>
.header-brand { position: relative; flex-shrink: 0; z-index: 2; }
/* The stamp crosses the title/status boundary without being clipped by toolbar rows. */
.cs-header, .cs-header .v-toolbar__content, .cs-header .v-toolbar__extension { overflow: visible; }
#header-demo-stamp { position: absolute; right: -10px; bottom: -14px; z-index: 10; }
</style>

<style>
@media (max-width: 700px) {
  /* Keep the initial Vuetify layout spacer, but let the title scroll with the document. */
  .cs-header.v-app-bar { position: absolute !important; }
}
</style>
